// client/src/components/AuditPanel.jsx
// Shows the Layer 2 protection audit status, last run info,
// mismatch count, and a manual "Run Audit" trigger button.
// Enhanced to show per-line-item details matching the Watchdog/Command pipeline.

import React, { useState } from "react";
import { formatEasternTime, getEasternTimeZoneLabel } from "../utils/timezone";

const AuditPanel = ({
  summary,
  results,
  auditRunning,
  onRunAudit,
  loading,
}) => {
  const s = summary || {};
  const lastRunAt = s.lastRunAt ? new Date(s.lastRunAt) : null;
  const durationSec = s.lastRunDurationMs ? (s.lastRunDurationMs / 1000).toFixed(1) : null;

  return (
    <div style={{
      background: "white",
      borderRadius: 16,
      border: "1px solid #e8ecf0",
      overflow: "hidden",
      marginBottom: 24,
    }}>
      {/* Header */}
      <div style={{
        padding: "16px 24px",
        background: "linear-gradient(135deg, #0f172a, #1e293b)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "rgba(255,255,255,0.1)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18,
          }}>
            {"\uD83D\uDEE1\uFE0F"}
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15, color: "white" }}>
              Layer 2 Protection — Location Audit
            </div>
            <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>
              Cross-checks ShipHero warehouse assignments against Shopify fulfillment locations
            </div>
          </div>
        </div>
        <button
          onClick={onRunAudit}
          disabled={auditRunning || loading}
          style={{
            padding: "9px 20px", borderRadius: 10, fontSize: 13, fontWeight: 700,
            border: "none",
            background: auditRunning ? "rgba(255,255,255,0.1)" : "#6366f1",
            color: auditRunning ? "#64748b" : "white",
            cursor: auditRunning ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          {auditRunning ? (
            <>
              <SpinnerIcon />
              Auditing...
            </>
          ) : "\u25B6 Run Audit Now"}
        </button>
      </div>

      {/* Stats row */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(4, 1fr)",
        borderBottom: "1px solid #f1f5f9",
      }}>
        <StatCell
          label="Orders Checked"
          value={s.totalChecked ?? "\u2014"}
          color="#6366f1"
        />
        <StatCell
          label="Mismatches"
          value={s.mismatchCount ?? "\u2014"}
          color={s.mismatchCount > 0 ? "#ef4444" : "#10b981"}
          highlight={s.mismatchCount > 0}
        />
        <StatCell
          label="Actions Taken"
          value={s.actionsTriggered ?? "\u2014"}
          color="#f59e0b"
        />
        <StatCell
          label="Last Run"
          value={lastRunAt ? `${formatEasternTime(lastRunAt)} ${getEasternTimeZoneLabel()}` : "Never"}
          sub={durationSec ? `${durationSec}s duration` : null}
          color="#64748b"
        />
      </div>

      {/* Results table */}
      {results && results.length > 0 ? (
        <div style={{ overflowX: "auto" }}>
          <AuditResultsTable results={results} />
        </div>
      ) : (
        <div style={{
          padding: "40px 24px", textAlign: "center",
          color: "#94a3b8", fontSize: 14,
        }}>
          {auditRunning ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <SpinnerIcon size={24} />
              <span>Audit in progress — fetching orders from ShipHero...</span>
            </div>
          ) : (
            "No audit results yet. Click \"Run Audit Now\" to start."
          )}
        </div>
      )}
    </div>
  );
};

// ─── Results Table with expandable rows ───────────────────────────

function AuditResultsTable({ results }) {
    const [expandedIds, setExpandedIds] = useState(new Set());

    const toggleExpand = (id) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    return (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
                <tr style={{ background: "#f8fafc" }}>
                    <th style={thStyle}></th>
                    <th style={thStyle}>Order</th>
                    <th style={thStyle}>Shopify Location</th>
                    <th style={thStyle}>SH Status</th>
                    <th style={thStyle}>Line Items</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Action</th>
                    <th style={thStyle}>Audited</th>
                </tr>
            </thead>
            <tbody>
                {results.map((r, i) => {
                    const id = r.orderId || i;
                    const expanded = expandedIds.has(id);
                    const lineItems = r.lineItemValidation || [];
                    const hasMismatchItems = lineItems.some(li => li.should_cancel);

                    return (
                        <React.Fragment key={id}>
                            <tr
                                style={{
                                    borderTop: "1px solid #f1f5f9",
                                    background: r.matched === false
                                        ? "#fff5f5"
                                        : i % 2 === 0 ? "white" : "#fafbfc",
                                    cursor: lineItems.length > 0 ? "pointer" : "default",
                                }}
                                onClick={() => lineItems.length > 0 && toggleExpand(id)}
                            >
                                <td style={{ ...tdStyle, width: 30, textAlign: "center", color: "#94a3b8" }}>
                                    {lineItems.length > 0 ? (expanded ? "\u25BC" : "\u25B6") : ""}
                                </td>
                                <td style={tdStyle}>
                                    <span style={{ fontWeight: 700, color: "#0f172a" }}>
                                        {r.orderNumber || r.orderId}
                                    </span>
                                </td>
                                <td style={tdStyle}>
                                    {hasMismatchItems ? (
                                        <MismatchLocationBadges lineItems={lineItems} />
                                    ) : (
                                        <LocationCell id={r.shopifyLocationId} name={r.shopifyLocationName} />
                                    )}
                                </td>
                                <td style={tdStyle}>
                                    <ShStatusBadge status={r.shipheroStatus} />
                                </td>
                                <td style={tdStyle}>
                                    {lineItems.length > 0 ? (
                                        <span style={{
                                            fontSize: 12, fontWeight: 600,
                                            color: r.mismatchedItems > 0 ? "#ef4444" : "#64748b",
                                        }}>
                                            {r.mismatchedItems > 0
                                                ? `${r.cancelledCount || r.mismatchedItems}/${r.totalLineItems} cancelled`
                                                : `${r.totalLineItems} items`
                                            }
                                        </span>
                                    ) : (
                                        <span style={{ color: "#94a3b8" }}>{"\u2014"}</span>
                                    )}
                                </td>
                                <td style={tdStyle}>
                                    <MatchBadge matched={r.matched} />
                                </td>
                                <td style={tdStyle}>
                                    <ActionBadge action={r.action} />
                                </td>
                                <td style={{ ...tdStyle, color: "#94a3b8", fontSize: 12 }}>
                                    {r.auditedAt ? `${formatEasternTime(r.auditedAt)} ${getEasternTimeZoneLabel()}` : "\u2014"}
                                </td>
                            </tr>
                            {/* Expanded line item detail */}
                            {expanded && lineItems.length > 0 && (
                                <tr>
                                    <td colSpan={8} style={{ padding: 0, background: "#f8fafc" }}>
                                        <LineItemDetail lineItems={lineItems} />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
    );
}

// ─── Line Item Detail Table ──────────────────────────────────────

function LineItemDetail({ lineItems }) {
    return (
        <div style={{ padding: "8px 24px 16px 54px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                    <tr>
                        <th style={liThStyle}>SKU</th>
                        <th style={liThStyle}>Product</th>
                        <th style={liThStyle}>Qty</th>
                        <th style={liThStyle}>SH Fulfillment</th>
                        <th style={liThStyle}>Location</th>
                        <th style={liThStyle}>Mismatch</th>
                        <th style={liThStyle}>Result</th>
                    </tr>
                </thead>
                <tbody>
                    {lineItems.map((li, i) => (
                        <tr key={i} style={{
                            background: li.should_cancel ? "#fff5f5" : "transparent",
                        }}>
                            <td style={liTdStyle}>
                                <span style={{ fontFamily: "monospace", fontSize: 11 }}>{li.sku || "\u2014"}</span>
                            </td>
                            <td style={liTdStyle} title={li.product_name}>
                                {(li.product_name || "").substring(0, 45)}
                            </td>
                            <td style={{ ...liTdStyle, textAlign: "center" }}>{li.quantity}</td>
                            <td style={liTdStyle}>
                                <SmallStatusBadge status={li.shiphero_fulfillment_status} />
                            </td>
                            <td style={liTdStyle}>
                                <span style={{ fontWeight: li.location_mismatch ? 700 : 400, color: li.location_mismatch ? "#ef4444" : "#475569" }}>
                                    {li.assigned_location_name || "\u2014"}
                                </span>
                            </td>
                            <td style={{ ...liTdStyle, textAlign: "center" }}>
                                {li.skip_reason
                                    ? <span style={{ color: "#94a3b8", fontSize: 10 }}>{li.skip_reason}</span>
                                    : li.location_mismatch
                                        ? <span style={{ color: "#ef4444", fontWeight: 700 }}>YES</span>
                                        : <span style={{ color: "#10b981" }}>No</span>
                                }
                            </td>
                            <td style={liTdStyle}>
                                {li.skip_reason === "already_cancelled"
                                    ? <SmallBadge bg="#e2e8f0" color="#475569" label="ALREADY CANCELLED" />
                                    : li.skip_reason
                                        ? <SmallBadge bg="#e2e8f0" color="#475569" label="SKIPPED" />
                                        : li.should_cancel
                                            ? <SmallBadge bg="#fee2e2" color="#991b1b" label={"\u2705 CANCELLED"} />
                                            : <SmallBadge bg="#dcfce7" color="#166534" label="KEEP" />
                                }
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Sub-components ───────────────────────────────────

const MismatchLocationBadges = ({ lineItems }) => {
    const mismatched = lineItems.filter(li => li.location_mismatch || li.should_cancel);
    const uniqueLocs = [...new Set(mismatched.map(li => li.assigned_location_name).filter(Boolean))];
    const okCount = lineItems.length - mismatched.length;
    return (
        <div style={{ lineHeight: "1.4" }}>
            {uniqueLocs.map((loc, i) => (
                <span key={i} style={{
                    display: "inline-block", padding: "2px 8px", borderRadius: 6,
                    fontSize: 11, fontWeight: 700,
                    background: "#fee2e2", color: "#991b1b", marginRight: 4,
                }}>
                    {"\u26A0"} {loc}
                </span>
            ))}
            {okCount > 0 && (
                <span style={{ color: "#94a3b8", fontSize: 10 }}>+{okCount} ok</span>
            )}
        </div>
    );
};

const SmallBadge = ({ bg, color, label }) => (
    <span style={{
        display: "inline-block", padding: "1px 6px", borderRadius: 4,
        fontSize: 10, fontWeight: 700, background: bg, color,
    }}>
        {label}
    </span>
);

const SmallStatusBadge = ({ status }) => {
    if (!status) return <span style={{ color: "#94a3b8" }}>{"\u2014"}</span>;
    const s = status.toLowerCase();
    const color = s === "pending" ? "#0284c7"
        : s === "fulfilled" || s === "shipped" ? "#059669"
        : s === "cancelled" || s === "canceled" ? "#dc2626"
        : "#64748b";
    const bg = s === "pending" ? "#e0f2fe"
        : s === "fulfilled" || s === "shipped" ? "#dcfce7"
        : s === "cancelled" || s === "canceled" ? "#fee2e2"
        : "#f1f5f9";
    return <SmallBadge bg={bg} color={color} label={status} />;
};

const StatCell = ({ label, value, sub, color, highlight }) => (
  <div style={{
    padding: "18px 24px",
    borderRight: "1px solid #f1f5f9",
    background: highlight ? "#fff5f5" : "transparent",
  }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
      {label}
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color: highlight ? "#ef4444" : color, lineHeight: 1 }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
  </div>
);

const LocationCell = ({ id, name }) => (
  <span style={{
    padding: "3px 8px", borderRadius: 6, fontSize: 12,
    background: "#ede9fe", color: "#5b21b6", fontWeight: 600,
  }}>
    {name || id || "\u2014"}
  </span>
);

const ShStatusBadge = ({ status }) => {
    if (!status) return <span style={{ color: "#94a3b8" }}>{"\u2014"}</span>;
    const s = status.toLowerCase();
    const color = s === "pending" ? "#0284c7"
        : s === "fulfilled" || s === "shipped" ? "#059669"
        : s === "cancelled" || s === "canceled" ? "#dc2626"
        : s === "partial" ? "#7c3aed"
        : "#64748b";
    const bg = s === "pending" ? "#e0f2fe"
        : s === "fulfilled" || s === "shipped" ? "#dcfce7"
        : s === "cancelled" || s === "canceled" ? "#fee2e2"
        : s === "partial" ? "#ede9fe"
        : "#f1f5f9";
    return (
        <span style={{
            display: "inline-block", padding: "3px 8px", borderRadius: 6,
            fontSize: 11, fontWeight: 700, background: bg, color,
        }}>
            {status}
        </span>
    );
};

const MatchBadge = ({ matched }) => {
  if (matched === true)  return <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 12, background: "#dcfce7", color: "#166534", fontWeight: 700 }}>{"\u2713"} Match</span>;
  if (matched === false) return <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 12, background: "#fee2e2", color: "#991b1b", fontWeight: 700 }}>{"\u2717"} Mismatch</span>;
  return <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 12, background: "#f1f5f9", color: "#64748b", fontWeight: 600 }}>{"\u2014"}</span>;
};

const ActionBadge = ({ action }) => {
  const map = {
    SKIPPED_NO_SHOPIFY_MATCH: { bg: "#e2e8f0", color: "#334155", label: "Skipped (No Shopify)" },
    CANCELLED_AND_TAGGED: { bg: "#dcfce7", color: "#166534", label: "Cancelled + Tagged" },
    CANCELLED_AND_HELD: { bg: "#dcfce7", color: "#166534", label: "Cancelled + Held" },
    LINE_ITEMS_CANCELLED: { bg: "#dcfce7", color: "#166534", label: "Items Cancelled" },
    HOLD_APPLIED: { bg: "#dcfce7", color: "#166534", label: "Hold Applied" },
    NEUTRALIZATION_FAILED: { bg: "#fee2e2", color: "#991b1b", label: "Action Failed" },
    HOLD_FAILED:  { bg: "#fee2e2", color: "#991b1b", label: "Hold Failed" },
    SKIPPED_FULFILLED: { bg: "#e0f2fe", color: "#0369a1", label: "Skipped (Fulfilled)" },
    SKIPPED_CANCELLED: { bg: "#e2e8f0", color: "#334155", label: "Skipped (Cancelled)" },
    NONE:         { bg: "#f1f5f9", color: "#475569", label: "No Action" },
    ERROR:        { bg: "#fef3c7", color: "#92400e", label: "Error" },
  };
  const s = map[action] || map.NONE;
  return (
    <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 12, background: s.bg, color: s.color, fontWeight: 600 }}>
      {s.label}
    </span>
  );
};

const SpinnerIcon = ({ size = 14 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    style={{ animation: "spin 1s linear infinite", display: "inline-block" }}
  >
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="40" strokeDashoffset="15" />
  </svg>
);

const thStyle = {
  padding: "10px 16px", textAlign: "left",
  fontSize: 11, fontWeight: 700, color: "#94a3b8",
  textTransform: "uppercase", letterSpacing: "0.05em",
};

const tdStyle = { padding: "12px 16px" };

const liThStyle = {
    padding: "6px 10px", textAlign: "left",
    fontSize: 10, fontWeight: 600, color: "#94a3b8",
    textTransform: "uppercase", borderBottom: "1px solid #e2e8f0",
};

const liTdStyle = {
    padding: "6px 10px", color: "#475569",
    borderBottom: "1px solid #f1f5f9",
};

export default AuditPanel;
